interface HolidayShape {
  month: number;
  date: number;
  saturdayAdjustment: number;
  sundayAdjustment: number;
  boxingDaySundayAdjustment?: number;
  boxingDayMondayAdjustment?: number;
  locale: string;
}

const isStaticDateHoliday = (
  holidays: Array<HolidayShape>,
  month: number,
  date: number,
  dayOfWeek: number,
  year: number
): boolean => {
  /* First check is to eliminate Juneteenth if it is prior to 2021. Note: this logic will need to be
  made more specific if a new holiday is ever added to the month of June. */
  if (year < 2021 && month === 5) {
    return false;
  }
  /* Second check is for Fridays. */
  if (dayOfWeek === 5) {
    return holidays.some((holiday) => {
      /* If a US holiday lands on a Saturday and has a saturdayAdjustment which moves it to a Friday,
     this will return true if the date equals the US holiday date plus the saturdayAdjustment. */
      if (holiday.locale === 'en-us') {
        return (
          holiday.month === month &&
          (holiday.date === date ||
            holiday.date + holiday.saturdayAdjustment === date)
        );
      }
      /* Otherwise, if a holiday lands on a Friday it will return true. */
      return holiday.month === month && holiday.date === date;
    });
  }
  /* Third check is for Mondays. */
  if (dayOfWeek === 1) {
    /* If a US holiday lands on a Monday, this will return true, OR if a US holiday lands
    on a Sunday and has a sundayAdjustment that moves it to a Monday, this will return
    true if the date equals the US holiday date plus the sundayAdjustment. */
    return holidays.some(
      (holiday) =>
        (holiday.locale === 'en-us' &&
          holiday.month === month &&
          (holiday.date === date ||
            holiday.date + holiday.sundayAdjustment === date)) ||
        /* If a Canadian holiday lands on a Monday, this will return true, OR if a Canadian
      holiday lands on either a Saturday and has a saturdayAdjustment or a Sunday and has
      a sundayAdjustment that moves it to a Monday, this will return
      true if the date equals the CA holiday plus the adjustment. */
        (holiday.locale === 'en-ca' &&
          holiday.month === month &&
          (holiday.date === date ||
            holiday.date + holiday.saturdayAdjustment === date ||
            holiday.date + holiday.sundayAdjustment === date))
    );
  }
  /* Fourth check is for Tuesdays. */
  if (dayOfWeek === 2) {
    /* If Boxing Day in Canada lands on either a Sunday or a Monday, it needs to be moved to
    a Tuesday so as not to overlap with Christmas which will have been adjusted from
    Saturday or Sunday to Monday. Returns true if the date is Tuesday and is Boxing Day
    plus the applicable adjustment. */
    return holidays.some(
      (holiday) =>
        (holiday.locale === 'en-ca' &&
          ('boxingDaySundayAdjustment' in holiday ||
            'boxingDayMondayAdjustment' in holiday) &&
          holiday.month === month &&
          (holiday.date + holiday.boxingDaySundayAdjustment === date ||
            holiday.date + holiday.boxingDayMondayAdjustment === date)) ||
        /* Otherwise, if a holiday lands on a Tuesday, it returns true. */
        (holiday.month === month && holiday.date === date)
    );
  }
  /* Fifth check is for Saturdays. */
  if (dayOfWeek === 6) {
    /* Returns true if a holiday lands on a Saturday and there is no saturdayAdjustment. */
    return holidays.some(
      (holiday) =>
        holiday.month === month &&
        holiday.date === date &&
        holiday.saturdayAdjustment === 0
    );
  }
  /* Sixth check is for Sundays. */
  if (dayOfWeek === 0) {
    /* Returns true if a holiday lands on a Sunday and there is no sundayAdjustment. */
    return holidays.some(
      (holiday) =>
        holiday.month === month &&
        holiday.date === date &&
        holiday.sundayAdjustment === 0
    );
  }
  /* Finally returns true for all other holidays that do not fall into one of the above. */
  return holidays.some(
    (holiday) => holiday.month === month && holiday.date === date
  );
};

export default isStaticDateHoliday;
